import { useReducer, Reducer } from 'react';
import { LoadingStatus } from 'models/LoadingStatus';
import { isValidEmail } from 'components/Shared/Form';
import { postSubscribe } from 'services/ApiNewsletterService';
import { rm } from 'utils';
import { GtmNewsletterSignup } from 'store/googleTagManager/actions';
import { GtmNewsletterPlacement } from 'store/googleTagManager/types';
import { useDispatch } from 'react-redux';

interface NewsletterFormState {
  email: string;
  loadingStatus: LoadingStatus;
  message?: string;
  showModal: boolean;
}

const initialState: NewsletterFormState = {
  email: '',
  loadingStatus: LoadingStatus.Idle,
  message: undefined,
  showModal: false
};

type Action =
  | {
      type: 'setEmail';
      email: string;
    }
  | { type: 'submit' }
  | { type: 'done'; message: string }
  | { type: 'error'; message: string }
  | { type: 'dismiss' };

const reducer: Reducer<NewsletterFormState, Action> = (state, action) => {
  switch (action.type) {
    case 'setEmail':
      return { ...state, email: action.email };
    case 'submit':
      return { ...state, loadingStatus: LoadingStatus.Fetching };
    case 'done':
      return {
        ...state,
        loadingStatus: LoadingStatus.Completed,
        showModal: true,
        message: action.message
      };
    case 'error':
      return {
        ...state,
        loadingStatus: LoadingStatus.Error,
        showModal: true,
        message: action.message
      };
    case 'dismiss':
      return { ...state, loadingStatus: LoadingStatus.Idle, showModal: false, message: undefined };
    default:
      return state;
  }
};

export const useNewsletterForm = (gtmSignupPlacement: GtmNewsletterPlacement) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const reduxDispatch = useDispatch();
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    dispatch({ type: 'setEmail', email: e.target.value });

  const subscribe = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    dispatch({ type: 'submit' });
    if (!isValidEmail(state.email)) {
      dispatch({ type: 'error', message: rm('error_uservalidation_emailformat') });
      return Promise.reject();
    }
    return postSubscribe(state.email.trim())
      .then(() => {
        dispatch({
          type: 'done',
          message: rm('js_bh_dialogs_younowsubscribetoournewsletter')
        });
        reduxDispatch(GtmNewsletterSignup(gtmSignupPlacement));
        return Promise.resolve();
      })
      .catch(() => {
        dispatch({ type: 'error', message: rm('error_uservalidation_emailformat') });
        return Promise.reject();
      });
  };

  const close = () => dispatch({ type: 'dismiss' });

  const isFetching = state.loadingStatus === LoadingStatus.Fetching;
  const isComplete = state.loadingStatus === LoadingStatus.Completed;
  const isError = state.loadingStatus === LoadingStatus.Error;
  const resetEmail = () => {
    dispatch({ type: 'setEmail', email: initialState.email });
  };

  return {
    handleChange,
    subscribe,
    close,
    showModal: state.showModal,
    message: state.message,
    email: state.email,
    resetEmail,
    isFetching,
    isComplete,
    isError
  };
};
